import React, { useEffect, useState } from 'react';
import groupBy from 'lodash/groupBy';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { Link } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import ADMISSION_TYPES from 'constant/admission-types';
import STATUSES from 'constant/college-application-status';
import { API } from 'aws-amplify';
import Skeleton from '@material-ui/lab/Skeleton';
import { UPDATE_SCHOOL } from 'graphql/student_college';
import { useMutation, Query } from 'react-apollo';
import DatePickerLabel from 'components/common/DatePickerLabel';
import Dropdown from 'components/form/Dropdown';
import { GET_COLLEGE_DEADLINES, UPDATE_DEADLINE } from 'graphql/colleges';
import useSnack from 'hooks/useSnack';
import Deadline from './Deadline';

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.05)',
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderLeft: '5px solid',
    borderColor: (props) => props.color || theme.palette.text.secondary,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  linkReset: {
    color: 'inherit'
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  SATscore : {
    color: theme.palette.success.main
  }
}));

const SchoolCard = ({
  school,
  onAdmissionTypeChange,
  onStatusChange,
  index,
  user,
  showScores,
  showDeadlines,
  color,
}) => {
  color=STATUSES.find((s) => s.value === school.status)?.color
  const classes = useStyles({ color });
  const snack = useSnack();
  const canEdit = user.type !== 'parent';
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateSchool] = useMutation(UPDATE_SCHOOL);
  const [updateDeadline] = useMutation(UPDATE_DEADLINE);
  const canReportDeadline = !['parent', 'student'].includes(user.type);

  const onDateChange = (date) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          app_deadline: date.format('YYYY-MM-DD'),
        },
      },
    });
  };

  const getScores = async () => {
    await API.get('api', `/college/${school.college_id}`)
      .then((school) => {
        return {
          cr_lower:
            school[
              'latest.admissions.sat_scores.25th_percentile.critical_reading'
            ],
          cr_higher:
            school[
              'latest.admissions.sat_scores.75th_percentile.critical_reading'
            ],
          math_lower:
            school['latest.admissions.sat_scores.25th_percentile.math'],
          math_higher:
            school['latest.admissions.sat_scores.75th_percentile.math'],
          act_lower:
            school['latest.admissions.act_scores.25th_percentile.cumulative'],
          act_higher:
            school['latest.admissions.act_scores.75th_percentile.cumulative'],
        };
      })
      .then((data) => {
        setScores(data);
        setLoading(false);
      });
  };

  const onReportDeadline = (deadline, suggestedDate, popupState) => () => {
    popupState.close();
    if (!suggestedDate) {
      return snack.warning('Please suggest a date');
    }
    updateDeadline({
      variables: {
        id: deadline.id,
        data: {
          flagged: true,
          flagged_by: user.id,
          suggested_date: suggestedDate,
        },
      },
    })
      .then(() => snack.success('Deadline reported'))
      .catch((e) => e.message);
  };

  useEffect(() => {
    if(showScores && !scores) {
      getScores();
    }
  }, [showScores]);

  return (
    <Draggable
      draggableId={String(school.id)}
      index={index}
      isDragDisabled={!canEdit}
    >
      {(provided) => (
        <div
          className={classes.card}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Box {...provided.dragHandleProps}>
            <Typography gutterBottom variant="subtitle2" color="textPrimary" component="p">
              {canEdit ? (
                <Link to={`list/${school.college_id}`} className={classes.linkReset}>
                  {school.college_name}
                </Link>
              ) : (
                school.college_name
              )}
            </Typography>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <DatePickerLabel
                disabled={!canEdit}
                value={school.app_deadline}
                onChange={onDateChange}
                emptyLabel="Select Due Date"
              />
            </Box>
          </Box>
          <Box display="flex" marginBottom={1}>
            <Dropdown
              disabled={!canEdit}
              color={color}
              options={STATUSES}
              value={school.status}
              onChange={(val) => onStatusChange(val, school)}
            />
            <Dropdown
              disabled={!canEdit}
              showValue
              options={ADMISSION_TYPES}
              value={school.admission_type}
              onChange={(val) => onAdmissionTypeChange(val, school)}
            />
          </Box>
          {showScores && (
            <Box
              display="flex"
              flexDirection="column"
              {...provided.dragHandleProps}
            >
              {loading ? (
                <Skeleton animation="wave" />
              ) : (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  marginTop={2}
                >
                  <Typography variant="small">
                    Avg SAT Math
                  </Typography>
                  <Typography className={classes.SATscore} variant="small">
                    {(scores?.math_higher + scores?.math_lower) / 2}
                  </Typography>
                </Box>
              )}
              {loading ? (
                <Skeleton animation="wave" />
              ) : (
                <Box
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography variant="small">
                    Avg SAT Critical Reading
                  </Typography>
                  <Typography className={classes.SATscore} variant="small">
                    {(scores?.cr_higher + scores?.cr_lower) / 2}
                  </Typography>
                </Box>
              )}
              {loading ? (
                <Skeleton animation="wave" />
              ) : (
                <Box
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography variant="small">Avg ACT Score</Typography>
                  <Typography variant="small">
                    {(scores?.act_higher + scores?.act_lower) / 2}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {/* </Collapse> */}
          {showDeadlines && (
            <Query
              query={GET_COLLEGE_DEADLINES}
              variables={{ id: school.college_id }}
            >
              {({ data, loading }) => {
                if (loading) return <Skeleton animation="wave" />;

                const grouped = groupBy(data?.rows, 'program_type');

                return (
                  <Box>
                    <Divider className={classes.divider} />
                    { Object.keys(grouped).map((type, idx) => (
                      <Deadline
                        key={idx}
                        type={type}
                        canReportDeadline={canReportDeadline}
                        deadlines={grouped[type]}
                        onSubmit={onReportDeadline}
                        user={user}
                      />
                    ))}
                  </Box>
                );
              }}
            </Query>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default SchoolCard;
